import { t, useI18n } from "@bookingcom/lingojs-react";
import { UIFlightData } from "@flights/types";
import usePointOfSale from "../../../../../../../hooks/usePointOfSale";
import useUserAgent from "../../../../../../../hooks/useUserAgent";
import useGetUrlParams from "../../../../../../../hooks/useGetUrlParams";
import { PRE_OPTED_ANCILLARIES_QUERY_PARAMS } from "../../../../../../../constants";
import flights_apex_web_cfar_on_sr_fd_v2 from "../../../../../../../utils/experiments/apex/flights_apex_web_cfar_on_sr_fd_v2";
import { isOfMetaOrigin } from "../../../../../../../utils/marketing-url-params";
import flights_apex_web_show_included_baggage_asap from "../../../../../../../utils/experiments/apex/flights_apex_web_show_included_baggage_asap";

const useExtraProductsInfoController = (flight: UIFlightData) => {
  const i18n = useI18n();
  const pointOfSale = usePointOfSale();
  const { isMobile } = useUserAgent();
  const { getUrlParam } = useGetUrlParams();

  const { ancillaries, includedProducts, travellers } = flight;
  const { checkedInBaggage, cabinBaggagePerTraveller, flexibleTicket, travelInsurance, cancelForAnyReason } =
    ancillaries ?? {};

  const isFlexTicket = !!getUrlParam(PRE_OPTED_ANCILLARIES_QUERY_PARAMS.flexibleTicket);
  const isCfarTicket = !!getUrlParam(PRE_OPTED_ANCILLARIES_QUERY_PARAMS.cancelForAnyReason);

  const canShowCfarTicket = !!cancelForAnyReason && flights_apex_web_cfar_on_sr_fd_v2.isEligablePos(pointOfSale);
  const showCfarTicket = canShowCfarTicket && !!flights_apex_web_cfar_on_sr_fd_v2.trackWithDefaultStage();

  if (canShowCfarTicket) {
    flights_apex_web_cfar_on_sr_fd_v2.trackFlightDetailsStages(isMobile);
  }

  const showInsurance = pointOfSale !== "gb" && !!travelInsurance;
  const checkedInBaggageOption = checkedInBaggage?.options[0];
  const showExtraBaggage = Boolean(cabinBaggagePerTraveller || checkedInBaggage);

  const extrasRef = flights_apex_web_show_included_baggage_asap.useTrackStagesExtras(showExtraBaggage);

  // If the checked baggage has a genius discount, do not show it in this section. It will be in genius rewards.
  const checkedBaggageHasGeniusDeal = checkedInBaggageOption?.priceBreakdown?.bcomPricingItems?.some(
    (item) => item.itemType === "GENIUS_CHECKED_BAG"
  );

  // If the flex ticket has a genius discount, do not show it in this section. It will be in genius rewards.
  const showFlexibleTicket =
    !!flexibleTicket &&
    !flexibleTicket?.priceBreakdown?.bcomPricingItems?.some((item) => item.itemType === "GENIUS_FLEXTICKET");

  const showNonBaggageItems = showInsurance || showFlexibleTicket;

  const showLoadingPrice = isOfMetaOrigin();

  const data = {
    inconsistentCabinBaggageInfo: showExtraBaggage && includedProducts,
    cabinBaggagePerTraveller: showExtraBaggage && cabinBaggagePerTraveller,
    /* On flight details we only get one checked baggage option which is the cheapest one */
    checkedInBaggage: showExtraBaggage && !checkedBaggageHasGeniusDeal && checkedInBaggageOption,
    flexTicket: showFlexibleTicket && !(isFlexTicket || isCfarTicket) && flexibleTicket,
    cancelForAnyReason: showCfarTicket && cancelForAnyReason?.priceBreakdown?.total && cancelForAnyReason,
    insurance: pointOfSale !== "gb" && travelInsurance
  };

  const items = Object.values(data).filter((item) => !!item);

  return {
    header: i18n.trans(
      !showExtraBaggage || showNonBaggageItems ? t("flights_apex_fd_extras_headline") : t("flights_extra_baggage_title")
    ),
    subheader: showNonBaggageItems && i18n.trans(t("flights_apex_fd_extras_sub_heading")),
    hasData: items.length > 0,
    ancillaries,
    showLoadingPrice,
    travellers,
    extrasRef,
    ...data
  };
};

export default useExtraProductsInfoController;
